import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AssociationSource, TypedIdAndIdentity } from "common/associations/association-browser.service";
import { Association, AssociationTarget } from "common/associations/association-list.component";
import { AssociationStore } from "common/associations/association-store.interface";
import { AssociationTargetSearchQuery, AssocSearchResult } from "common/associations/search-query.logic";
import { SearchQuery } from "common/autoCompleteSearch/search-query";
import { DialogService } from "common/dialog/dialog.service";
import { AutoCompleteSearchComponent } from "common/autoCompleteSearch/auto-complete-search.component";
import { assertDefined } from "core/functions";
import { EnactmentAdapter } from "common/enactment/enactment-adapter.logic";

interface AssocSearchResultWithActions extends AssocSearchResult {
	actionsFromProcess?: Record<string, boolean>;
}

@Component({
	selector: "stages-change-source-element",
	templateUrl: "./change-source-element.component.html",
})
export class ChangeSourceElementComponent<S extends AssociationSource<T>, T extends AssociationTarget>
	implements AfterViewInit
{
	@Input()
	sourceElementContainer!: S;

	@Input()
	associationToChange!: Association<T>;

	@Input()
	associationType!: string;

	@Input()
	existingAssociations!: Association<T>[];

	@Input()
	sourceRole?: string;

	@Input()
	restrictAssociateDependentElementTypes?: string[];

	@Input()
	associationStore!: AssociationStore<S, T>;

	@Input()
	path!: string;

	@Output() readonly addedCallback = new EventEmitter<Association<T>>();

	@Output() readonly cancelled = new EventEmitter<void>();

	@ViewChild("autoComplete")
	autoCompleteComponent!: AutoCompleteSearchComponent<AssocSearchResult>;

	dependentElementContainer?: AssocSearchResultWithActions;

	localWorkspaceId!: string;
	localProcessVersion!: string;

	constructor(
		private route: ActivatedRoute,
		private readonly dialogService: DialogService,
		private readonly translateService: TranslateService,
	) {}

	async ngAfterViewInit(): Promise<void> {
		assertDefined(this.sourceElementContainer);
		assertDefined(this.associationToChange);
		assertDefined(this.associationToChange.targetElement);
		assertDefined(this.associationToChange.sourceElement);
		assertDefined(this.associationToChange.sourceElement!.process);
		assertDefined(this.existingAssociations);
		assertDefined(this.associationStore);
		this.localWorkspaceId = this.route.snapshot.paramMap.get("workspaceId")!;
		this.localProcessVersion = this.route.snapshot.paramMap.get("processVersion")!;
		if (this.sourceElementContainer instanceof EnactmentAdapter) {
			this.sourceElementContainer = (this.sourceElementContainer as EnactmentAdapter).element as unknown as S;
		}
		try {
			// get the selected sourceElement from the search and set it as dependentElementContainer
			const results = await this.getSearchQuery().run(this.sourceElementContainer.label!, ["process_element"]);
			const optionalTargetElement = results.filter(
				(result) => result.identity && result.identity === this.sourceElementContainer.identity,
			);
			if (optionalTargetElement.length === 1) {
				this.dependentElementContainer = optionalTargetElement[0];
			}
		} catch (e: unknown) {
			this.autoCompleteComponent.passErrorToResults(e);
			this.autoCompleteComponent.searchEngineAvailable = false;
		}
	}

	getSearchQuery(): SearchQuery<AssocSearchResult> {
		return new AssociationTargetSearchQuery(
			this.translateService,
			this.associationStore,
			this.sourceElementContainer.type.ident,
			[],
			this.getTargetElementsToIgnore(),
			this.localWorkspaceId,
			this.localWorkspaceId,
			this.localProcessVersion,
			this.associationType !== "enacts",
			false,
			this.restrictAssociateDependentElementTypes,
			this.dependentElementContainer ?? undefined,
			this.dependentElementContainer ? this.dependentElementContainer.id : undefined,
			[this.associationToChange.sourceElement!.process!.id],
			true,
		);
	}

	getTargetElementsToIgnore(): TypedIdAndIdentity[] {
		return this.existingAssociations
			.filter((assoc) => assoc.targetElement!.identity === this.associationToChange.targetElement!.identity)
			.map((assoc) => assoc.sourceElement!);
	}

	getIconClasses(element: AssocSearchResult, additionalClasses?: string): string[] {
		return this.associationStore.getInputClasses(
			element.type.ident,
			!!this.dependentElementContainer,
			element.type.subtypeIdent,
			additionalClasses,
		);
	}

	getIconClassesAndCheckForDependentType(element: AssocSearchResult, additionalClasses?: string): string[] {
		const iconClasses = this.associationStore.getInputClasses(
			element.type.ident,
			!!this.dependentElementContainer,
			element.type.subtypeIdent,
			additionalClasses,
		);

		if (element.isNew) {
			iconClasses.push("add");
			if (element.dependent) {
				iconClasses.push("ico-add");
			}
		} else if (!element.isNew && element.dependent) {
			iconClasses.push("ico-tag");
		}

		return iconClasses;
	}

	getLabelClasses(element: AssocSearchResult, additionalClasses?: string): string[] {
		return this.associationStore.getQuickAssignLabelClasses(element, additionalClasses);
	}

	getInputIconClasses(): string[] {
		return this.associationStore.getInputClasses(
			this.sourceElementContainer.type.ident,
			!!this.dependentElementContainer,
			undefined,
			"ico",
		);
	}

	addNewAssociationToList(association: Association<T>): void {
		this.addedCallback.emit(association);
	}

	async selectItemCallback(element: AssocSearchResult): Promise<void> {
		if (element.isNew) {
			if (this.elementWithSameLabelExists(element)) {
				if (await this.confirmCreationOfElementWithSameName(element)) {
					await this.createAssociationFromNewSourceElement(element);
				}
			} else {
				await this.createAssociationFromNewSourceElement(element);
			}
		} else {
			await this.createAssociationToExistingElement(element);
		}
	}

	private async confirmCreationOfElementWithSameName(element: AssocSearchResult): Promise<boolean> {
		return this.dialogService.confirm(
			"search.autoComplete.assocTarget.sameNameExistsCreateConfirm",
			{ name: element.label },
			"create",
			"cancel",
			false,
		);
	}

	private async createAssociationFromNewSourceElement(element: AssocSearchResult): Promise<void> {
		const assoc = await this.associationStore.createElementAndChangeAssociationSource(
			this.sourceElementContainer,
			element.type.ident,
			element.type.subtypeIdent!,
			element.name,
			this.localWorkspaceId,
			this.localProcessVersion,
			element.dependent,
			this.associationToChange.id,
			this.associationToChange.targetElement!.type.ident,
			this.associationToChange.targetElement!.id,
		);
		this.addNewAssociationToList(assoc);
	}

	private async createAssociationToExistingElement(element: AssocSearchResult): Promise<void> {
		const assoc = await this.associationStore.changeAssociationSourceOrTarget(
			this.sourceElementContainer,
			this.associationToChange.id,
			element.type.ident,
			element.id!,
			this.associationToChange.targetElement!.type.ident,
			this.associationToChange.targetElement!.id,
			this.localWorkspaceId,
			this.localProcessVersion,
			this.path,
		);
		this.addNewAssociationToList(assoc);
	}

	private elementWithSameLabelExists(element: AssocSearchResult): boolean {
		return (
			this.autoCompleteComponent.subordinatedResults!.filter(
				(targetElement) => targetElement.name.toLowerCase() === element.name.toLowerCase() && !targetElement.isNew,
			).length > 0
		);
	}

	cancel(): void {
		this.cancelled.emit();
	}
}
